import {
  useTableFilterSortSearchManager,
  useTableFilterSortData,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { useCallback, useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import FileSaver from 'file-saver';
import { parseRowValues } from './utils';
import { HEADERS } from './constants';
import { useFetchAllUnitAvailablity } from './useFetchAllUnitAvailability';
import UnitService from '../../services/unitService';
import moment from 'moment';
import { exportAndDownloadCSV } from '../../utils/csv-helpers';
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';
import useUniqueTableName from '../../hooks/useUniqueTableName';
import useNewColumnsVisibleByDefault from '../../hooks/useNewColumnsVisibleByDefault';
import useActionsFirstColumnOrder from '../../hooks/useActionsFirstColumnOrder';
import {
  accessibilityTypeCell,
  ADA_COLUMN_IDS,
  formatADA,
  formatAccessibilityTypes,
  shouldForceADAColumnOrder,
} from '../../utils/ada-helpers';

const PROPERTY_PATH_MAP = {
  property: {
    path: 'property',
    dataType: 'string',
  },
  unit: {
    path: 'unit.number',
    dataType: 'string',
  },
  floorPlan: {
    path: 'floorPlan',
    dataType: 'string',
  },
  bedsBaths: {
    path: 'bedsBaths',
    dataType: 'string',
  },
  sqFeet: {
    path: 'sqFeet',
    dataType: 'number',
  },
  status: { path: 'status', dataType: 'string' },
  moveOut: {
    path: 'moveOut',
    dataType: 'date',
  },
  moveInReady: {
    path: 'moveInReady',
    dataType: 'date',
  },
  daysVacant: {
    path: 'daysVacant',
    dataType: 'number',
  },
  quotingRent: {
    path: 'quotingRent',
    dataType: 'number',
  },
  requiredUnitFees: {
    path: 'requiredUnitFees',
    dataType: 'number',
  },
  ada: {
    path: 'ada',
    dataType: 'string',
  },
  accessibilityType: {
    path: 'accessibilityType',
    dataType: 'string',
  },
};

const useGetFilterOptions = ({ results }) => {
  return useMemo(() => {
    const options = { propertyOptions: [], statusOptions: [] };

    if (results) {
      results.forEach(({ property, unitStatus }) => {
        if (property?.name?.length) options.propertyOptions.push(property.name);
        if (unitStatus?.description?.length)
          options.statusOptions.push(unitStatus.description);
      });

      options.propertyOptions = Array.from(new Set(options.propertyOptions))
        .sort()
        .map((propertyName) => ({
          text: propertyName,
          value: propertyName?.toLowerCase(),
        }));
      options.statusOptions = Array.from(new Set(options.statusOptions))
        .sort()
        .map((statusName) => ({
          text: statusName,
          value: statusName?.toLowerCase(),
        }));
    }

    return options;
  }, [results]);
};

const useHeaders = ({ propertyOptions, statusOptions, showADAColumns }) => {
  return useMemo(() => {
    return HEADERS.filter(
      ({ id }) => showADAColumns || !ADA_COLUMN_IDS.includes(id),
    ).map((header) => {
      if (header.id === 'property') header.filterOptions = propertyOptions;
      if (header.id === 'status') header.filterOptions = statusOptions;
      return header;
    });
  }, [propertyOptions, statusOptions, showADAColumns]);
};

const useAllUnitAvailability = ({ intl, currentUserId }) => {
  const name = useUniqueTableName('centralized-unit-availability');
  const { adaInfo } = useFlags();
  const { data, isLoading } = useFetchAllUnitAvailablity();

  const { propertyOptions, statusOptions } = useGetFilterOptions({
    results: data,
  });

  const headers = useHeaders({
    propertyOptions,
    statusOptions,
    showADAColumns: adaInfo,
  });

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    headers,
    initialOrderBy: 'moveInReady',
    initialOrder: 'ASC',
  });

  const parsedData = useParseData({ data, intl });

  const sortedAndFilteredResults = useTableFilterSortData({
    results: parsedData,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP,
  });

  const rows = useRows({ sortedAndFilteredResults, intl, currentUserId });

  const {
    initialColumnsOrder: actionsFirstColumnsOrder,
    shouldForceOverwrite: actionsFirstForceOverwrite,
  } = useActionsFirstColumnOrder({
    name,
    headers,
    forceOverwrite: shouldForceADAColumnOrder({
      tableName: name,
      showADAColumns: adaInfo,
    }),
  });

  // A saved column visibility that predates a column keeps it hidden; show it
  // once, without undoing the columns the user has hidden since.
  const { initialColumnsOrder, initialSelectedColumns, shouldForceOverwrite } =
    useNewColumnsVisibleByDefault({
      name,
      headers,
      introducedColumnIds: ADA_COLUMN_IDS,
      initialColumnsOrder: actionsFirstColumnsOrder,
      forceOverwrite: actionsFirstForceOverwrite,
    });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
    initialColumnsOrder,
    initialSelectedColumns,
    forceOverwrite: shouldForceOverwrite,
  });

  const { onCSVButtonClick } = useCSVPDFExport({
    propertyName: name,
    hasAnyFilters:
      Object.keys(filterState)?.length ||
      Object.keys(dateState)?.length ||
      Object.keys(searchState)?.length,
    filteredHeaders,
    rows,
    results: sortedAndFilteredResults,
  });

  return {
    allColumnsHidden,
    columnOptions,
    count: rows?.length ?? 0,
    dateState,
    filterState,
    filterTypeState,
    filteredHeaders,
    handleColumnChange,
    handleDateSubmit,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSortChange,
    headers: HEADERS,
    isLoading,
    name,
    order,
    orderBy,
    rows,
    searchState,
    selectedColumns,
    totalCount: data?.length ?? undefined,
    onCSVButtonClick,
  };
};

const generateQuote = async (unit, currentUserId) => {
  const unitId = unit.id;
  const applicantId = undefined; //payload.applicantId;
  const prospectId = undefined;
  const userId = currentUserId;
  const organizationId = unit?.property?.organizationId;
  const property = unit?.property;
  const unitService = new UnitService();
  const file = await unitService.getQuote(
    property.id,
    organizationId,
    unitId,
    userId,
    applicantId,
    prospectId,
  );
  await FileSaver.saveAs(
    file,
    `Quote-${property.name.replace(/[^a-zA-z0-9]+/gi, '-')}-${moment().format(
      'MM-DD-YYYY',
    )}.pdf`,
  );
};

export const useRows = ({ sortedAndFilteredResults, currentUserId }) => {
  return useMemo(
    () =>
      sortedAndFilteredResults?.map((row) => {
        return {
          property: {
            value: row.property,
          },
          unit: {
            variant: 'link',
            to: `/property/${row.unit.propertyId}/unit/${row.unit.id}?prev=available`,
            target: '_blank',
            value: row.unit.number,
          },
          floorPlan: {
            variant: 'link',
            to: `/property/${row.unit.propertyId}/floorplan/${row.floorPlanId}?prev=available`,
            target: '_blank',
            value: row.floorPlan,
          },
          bedsBaths: {
            value: row.bedsBaths,
          },
          sqFeet: {
            value: row.sqFeet,
            variant: 'number',
          },
          status: { value: row.status },
          moveOut: {
            variant: 'date',
            value: row.moveOut,
          },
          moveInReady: {
            variant: 'date',
            value: row.moveInReady,
          },
          notes: {
            variant: 'description',
            value: row.notes,
          },
          daysVacant: {
            variant: 'number',
            value: row.daysVacant,
          },
          quotingRent: {
            variant: 'currency',
            value: row.quotingRent,
          },
          requiredUnitFees: {
            variant: 'currency',
            value: row.requiredUnitFees,
          },
          ada: {
            value: row.ada,
          },
          accessibilityType: accessibilityTypeCell(row.accessibilityType),
          actions: {
            variant: 'menu',
            name: 'actionsMenu',
            iconName: 'BoltIcon',
            color: 'primary',
            options: [
              {
                text: 'Quote',
                value: row,
                onClick: () => {
                  generateQuote(row.unit, currentUserId);
                },
              },
            ],
          },
        };
      }),
    [sortedAndFilteredResults, currentUserId],
  );
};

export const useParseData = ({ data }) => {
  return useMemo(
    () =>
      data?.map((unit) => {
        const {
          property,
          floorPlan,
          floorPlanId,
          bedsBaths,
          notes,
          status,
          sqFeet,
          moveOut,
          moveInReady,
          daysVacant,
          quotingRent,
          requiredUnitFees,
        } = parseRowValues(unit);

        return {
          unit,
          property,
          floorPlan,
          floorPlanId,
          bedsBaths,
          notes,
          status,
          sqFeet,
          moveOut,
          moveInReady,
          daysVacant,
          quotingRent,
          requiredUnitFees,
          ada: formatADA(unit.isADA),
          accessibilityType: formatAccessibilityTypes(
            unit.isADA,
            unit.accessibilityTypes,
          ),
          actions: '',
        };
      }),
    [data],
  );
};

const useCSVPDFExport = ({
  filteredHeaders,
  rows,
  propertyName,
  hasAnyFilters,
}) => {
  const onCSVButtonClick = useCallback(() => {
    exportAndDownloadCSV({
      excludedHeaders: ['actions'],
      filteredHeaders,
      fileName: `AllUnitAvailability_${propertyName
        ?.split(' ')
        .join('-')}_${formatDateDB(new Date())}`,
      rows: rows.map((row) => ({
        ...row,
        unit: { value: row.unit?.value },
        floorPlan: { value: row.floorPlan?.value },
      })),
      hasAnyFilters,
    });
  }, [filteredHeaders, rows, hasAnyFilters, propertyName]);

  return { onCSVButtonClick };
};

export default useAllUnitAvailability;
